import { fabric } from 'fabric';
import { clearGuides, snapWidgetPosition } from './guides';
const INITIAL_WIDTH = 640;
const INITIAL_HEIGHT = 360;
const INITIAL_LEFT = 80;
const INITIAL_TOP = 100;

const widgetCleanups = new WeakMap<fabric.Canvas, Map<string, () => void>>();

// Replaces (and runs) any cleanup previously registered for the same widget,
// so recreating a widget does not leave its old listeners behind.
export const registerWidgetCleanup = (
  canvas: fabric.Canvas | null,
  className: string,
  cleanup: () => void,
) => {
  if (!canvas) return;
  let cleanups = widgetCleanups.get(canvas);
  if (!cleanups) {
    cleanups = new Map();
    widgetCleanups.set(canvas, cleanups);
  }
  cleanups.get(className)?.();
  cleanups.set(className, cleanup);
};

export const disposeWidget = (
  canvas: fabric.Canvas | null | undefined,
  className: string,
) => {
  const cleanups = canvas && widgetCleanups.get(canvas);
  cleanups?.get(className)?.();
  cleanups?.delete(className);
};

export const disposeWidgets = (canvas: fabric.Canvas) => {
  widgetCleanups.get(canvas)?.forEach((cleanup) => cleanup());
  widgetCleanups.delete(canvas);
};

export const getDimensions = (
  canvas?: fabric.Canvas | null,
  obj?: fabric.Object,
  propRatio?: number,
) => {
  const ratio = propRatio || canvas?.getZoom() || 1;
  const realWidth =
    (obj?.width && obj.scaleX && obj?.width * obj.scaleX) || INITIAL_WIDTH;
  const realHeight =
    (obj?.height && obj.scaleY && obj?.height * obj.scaleY) || INITIAL_HEIGHT;
  const realLeft = obj?.left || INITIAL_LEFT;
  const realTop = obj?.top || INITIAL_TOP;

  const width = realWidth * ratio;
  const height = realHeight * ratio;
  const left = realLeft * ratio;
  const top = realTop * ratio;

  return {
    ratio,
    realWidth,
    realHeight,
    realLeft,
    realTop,
    width,
    height,
    left,
    top,
  };
};

export const handleRatio = (
  iframeDimensions: {
    width: number;
    height: number;
    left: number;
    top: number;
  },
  element: HTMLElement & { width?: string; height?: string },
) => {
  const { width, height, left, top } = iframeDimensions;
  element.style.cssText += `width: ${width}px; height: ${height}px; left: ${left}px; top: ${top}px;`;
  element.width = `${width}`;
  element.height = `${height}`;
};

export const dragElement = (
  element: HTMLDivElement,
  iframe: HTMLIFrameElement,
  canvas: fabric.Canvas | null,
  handleHistory?: () => void,
) => {
  if (!element) {
    return;
  }
  let activeObject = null as fabric.Object | null;
  let dragStart: {
    x: number;
    y: number;
    left: number;
    top: number;
    moved: boolean;
  } | null = null;

  // Pointer capture keeps move/up events coming to the overlay even when the
  // pointer is over an iframe, where document mouse listeners never fire.
  const dragPointerDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    const canvasObjects = canvas?.getObjects() || [];
    const iframeObject = canvasObjects.find(
      (canvasObject) => canvasObject.data?.className === iframe.className,
    );
    if (iframeObject) {
      activeObject = iframeObject;
      canvas?.setActiveObject(activeObject);
      canvas?.requestRenderAll();
    }
    if (iframeObject?.lockMovementX) return;
    e.preventDefault();
    element.setPointerCapture(e.pointerId);
    dragStart = {
      x: e.clientX,
      y: e.clientY,
      left: element.offsetLeft,
      top: element.offsetTop,
      moved: false,
    };
  };

  const dragPointerMove = (e: PointerEvent) => {
    if (!dragStart) return;
    e.preventDefault();
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    if (deltaX || deltaY) dragStart.moved = true;
    let left = dragStart.left + deltaX;
    let top = dragStart.top + deltaY;
    if (canvas && activeObject) {
      const zoom = canvas.getZoom() || 1;
      const snapped = snapWidgetPosition(canvas, activeObject, {
        left: left / zoom,
        top: top / zoom,
      });
      left = snapped.left * zoom;
      top = snapped.top * zoom;
    }
    moveTo(left, top);
  };

  const endDrag = () => {
    if (!dragStart) return;
    const { moved } = dragStart;
    dragStart = null;
    if (canvas) clearGuides(canvas);
    if (activeObject && moved) {
      // @ts-ignore;
      activeObject.auto = false;
      handleHistory && handleHistory();
    }
    activeObject = null;
  };

  element.addEventListener('pointerdown', dragPointerDown);
  element.addEventListener('pointermove', dragPointerMove);
  element.addEventListener('pointerup', endDrag);
  element.addEventListener('lostpointercapture', endDrag);
  // `draggable` mirrors the lock state; a native drag would swallow the
  // pointer events, so it is never allowed to start.
  element.ondragstart = (e) => e.preventDefault();

  const moveTo = (left: number, top: number) => {
    element.style.left = `${left}px`;
    element.style.top = `${top}px`;
    iframe.style.left = `${left}px`;
    iframe.style.top = `${top}px`;

    if (activeObject) {
      const ratio = canvas?.getZoom() || 1;
      activeObject.set({ left: left / ratio, top: top / ratio });
      activeObject.setCoords();
      canvas?.requestRenderAll();
    }
  };

  return () => {
    element.removeEventListener('pointerdown', dragPointerDown);
    element.removeEventListener('pointermove', dragPointerMove);
    element.removeEventListener('pointerup', endDrag);
    element.removeEventListener('lostpointercapture', endDrag);
  };
};

// Widgets are HTML laid over the canvas; this moves a widget's overlay and
// iframe to where its canvas object is (also inside a multi-selection).
export const syncWidgetOverlay = (
  canvas: fabric.Canvas,
  object: fabric.Object,
) => {
  const className = object.data?.className;
  if (!className) return;
  const zoom = canvas.getZoom() || 1;
  const { left, top } = object.getBoundingRect(true, true);
  for (const element of Array.from(
    document.getElementsByClassName(className),
  ) as HTMLElement[]) {
    element.style.left = `${left * zoom}px`;
    element.style.top = `${top * zoom}px`;
  }
};
